import React, {ReactElement} from "react";
import {v4 as uuid} from "uuid";

import {visibleForLocale} from "/src/utils/utils";
import {css} from "/src/utils/utils";

import EmailSubscribe from "../email-subscribe/EmailSubscribe";
import CTAButton, {CTA} from "../buttons/cta-button/CTAButton";
import Video, {VideoType} from "../videos/video/Video";
import ModuleBody from "../layouts/module-body/ModuleBody";
import Icon from "../icon/Icon";
import Image from "../image/Image";

import {LOCALE} from "/src/config";
import {ImageObject} from "../image/Image";
import {SimpleFormProps} from "/src/types";

import PlayIcon from "/src/svg/play-icon-filled.svg";
import GetStartedDarkSVG from "/src/svg/get-started-dark.svg";
import GetStartedLightSVG from "/src/svg/get-started-light.svg";

import * as styles from "./get-started-module.module.scss";

export interface GetStartedModuleProps
  extends Omit<
    Queries.GetStartedModuleFragmentFragment,
    "layout" | "videoBehavior"
  > {
  layout: "plain" | "with_image" | "with_video";
  videoBehavior?: VideoType;
}

const GetStartedModule = (
  props: GetStartedModuleProps
): ReactElement | null => {
  const {
    title,
    body,
    eyebrow,
    ctaButtons,
    videoSource,
    videoThumbnail,
    image,
    layout = "plain",
    darkMode,
    form,
    videoControls,
    videoBehavior,
    visibleIn,
  } = props;

  if (!visibleForLocale(visibleIn as Array<string>, LOCALE)) {
    return null;
  }

  if (form != null) {
    return (
      <EmailSubscribe
        copy={{
          title: title!,
          description: body?.body!,
        }}
        darkMode={darkMode}
        image={image as ImageObject}
        subscriptionForm={form as SimpleFormProps}
      />
    );
  }

  const rightSection = () => {
    /*
      GROW-1511 if layout is plain, we will use default triangle svg on right side
    */
    switch (layout) {
      case "with_image":
        return (
          <div className={css(styles.getStartedModule__imgWrapper)}>
            {image != null ? (
              <Image
                objectFit="cover"
                image={image as ImageObject}
                classes={styles.getStartedModule__img}
              />
            ) : null}
          </div>
        );
      case "with_video":
        const videoType = videoBehavior || "inline";

        if (
          videoSource == null ||
          (videoType !== "autoplay" && videoThumbnail == null && image == null)
        ) {
          return null;
        }

        return (
          <div className={css(styles.getStartedModule__videoWrapper)}>
            <Video
              videoClasses={styles.getStartedModule__video}
              videoTitle={title!}
              videoSource={videoSource}
              customPlayIcon={PlayIcon}
              videoThumbnail={(videoThumbnail || image) as ImageObject}
              showControl={videoControls || true}
              type={videoType as VideoType}
              videoConfig={{
                wistia: {
                  options: {
                    fitStrategy: "cover",
                  },
                },
              }}
            />
          </div>
        );
      default:
        return (
          <Icon
            svg={darkMode ? GetStartedDarkSVG : GetStartedLightSVG}
            role="presentation"
            classes={css(styles.getStartedModule__bgIcon)}
          />
        );
    }
  };

  return (
    <section
      className={css(
        styles.getStartedModule,
        darkMode ? styles.getStartedModuleDark : "",
        layout === "plain" ? styles.getStartedModulePlain : ""
      )}
    >
      <ModuleBody classes={styles.getStartedModule__el}>
        <div className={css(styles.getStartedModule__content)}>
          {eyebrow != null ? (
            <p className={css("eyebrow", styles.getStartedModule__eyebrow)}>
              {eyebrow}
            </p>
          ) : null}
          {title != null ? (
            <h2 className={css("h2", styles.getStartedModule__headline)}>
              {title}
            </h2>
          ) : null}
          {body != null ? (
            <p className={css("p", styles.getStartedModule__body)}>
              {body?.body}
            </p>
          ) : null}
          {/* we will also assume that the first CTA would be primary, subsequent cta should be secondary */}
          {ctaButtons != null && (
            <ul className={css(styles.getStartedModule__list)}>
              {ctaButtons.map((cta, i) => (
                <li className={css(styles.getStartedModule__item)} key={uuid()}>
                  <CTAButton
                    darkMode={darkMode as boolean}
                    kind={i === 0 ? "secondary" : "tertiary"}
                    cta={cta as CTA}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
        {rightSection()}
      </ModuleBody>
    </section>
  );
};

export default GetStartedModule;
